/**
 * Leasing Verification — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/leasing-verification.schemas.ts`
 * imports and re-exports these to register the routes, and
 * `apps/landlord/src/lib/leasing.api.ts` derives its `VerificationAttachment` /
 * `VerificationTask` types from the same objects. See packages/api-types/README.md.
 *
 * Covers every route returning a VerificationTask (as `task`, or within `tasks`) or a
 * VerificationAttachment (as `attachment`, or within a task's `attachments`):
 *   GET   /landlord/api/leads/:id/applicants/:applicantId/verifications
 *   PATCH /landlord/api/leads/:id/applicants/:applicantId/verifications/:taskId
 *   POST  /landlord/api/leads/:id/applicants/:applicantId/verifications/:taskId/attachments
 */
import { z } from 'zod';
const uuid = z.uuid();
const cuid = z.string(); // cuid2 — for entity IDs
const dateString = z.string(); // ISO 8601 string
// Verification enums — mirrored as plain Zod enums (not cross-imported from the route-local
// module, which keeps its own copies for schemas outside this migration's scope, e.g.
// leasing-verification.schemas.ts's UpdateTaskBodySchema). Must match VerificationKind /
// VerificationStatus / UploadActorType in @resida/database.
const VerificationKindSchema = z.enum([
    'criminal_civil',
    'income',
    'rental_history',
    'government_id',
    'supporting_documents',
]);
const VerificationStatusSchema = z.enum([
    'pending',
    'requested',
    'in_progress',
    'passed',
    'failed',
    'waived',
]);
const UploadActorTypeSchema = z.enum(['manager', 'prospect']);
// ---------------------------------------------------------------------------
// VerificationAttachment response
// ---------------------------------------------------------------------------
export const VerificationAttachmentResponseSchema = z.object({
    id: uuid,
    verificationTaskId: uuid,
    storageKey: z.string(),
    url: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.string(), // BigInt serialized as string
    uploadedByActorType: UploadActorTypeSchema,
    uploadedByActorId: uuid.nullable(),
    uploadedAt: dateString,
    scheduledDisposalAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Active token presence (not the raw token)
// ---------------------------------------------------------------------------
const ActiveTokenPresenceSchema = z.object({
    hasActiveToken: z.boolean(),
    expiresAt: dateString.nullable(),
});
// ---------------------------------------------------------------------------
// VerificationTask response (Iteration 1: applicantId replaces leadId)
// ---------------------------------------------------------------------------
export const VerificationTaskResponseSchema = z.object({
    id: uuid,
    applicantId: cuid,
    kind: VerificationKindSchema,
    status: VerificationStatusSchema,
    notes: z.string().nullable(),
    updatedByUserId: uuid.nullable(),
    updatedAt: dateString,
    createdAt: dateString,
    attachments: z.array(VerificationAttachmentResponseSchema),
    activeToken: ActiveTokenPresenceSchema,
});
//# sourceMappingURL=verification.js.map